import 'react/jsx-runtime'

import { ReactNode, StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, createRoutesFromChildren, matchRoutes, Navigate, Outlet, RouteObject, RouterProvider, useLocation, useNavigationType } from 'react-router-dom'

import { CssBaseline, styled, Typography } from '@mui/material'
import { SWRConfig } from 'swr'
import { AppSettings } from './AppSettings'
import ServerSentEventsProvider from './components/api/ServerSentEventsProvider'
import ServiceStackClientProvider from './components/api/ServiceStackClientProvider'
import AuthenticatedUserProvider from './components/providers/authentication/AuthenticatedUserProvider'
import LoadingProvider from './components/providers/layouts/LoadingProvider'
import ChosenThemeProvider from './components/providers/styles/ChosenThemeProvider'
import ChosenThemeSizeProvider from './components/providers/styles/ChosenThemeSizeProvider'
import ThemeProvider from './components/providers/styles/ThemeProvider'
import DefaultAfterThemeCreated from './styles/DefaultAfterThemeCreated'
import DefaultBeforeThemeCreated from './styles/DefaultBeforeThemeCreated'

import * as Sentry from "@sentry/react"
import { AuthenticateResponse } from './api/dtos'
import RouteErrorBoundary from './components/errors/RouteErrorBoundary'
import Layout from './components/layouts/Layout'
import FavouritesProvider from './components/providers/data/FavouritesProvider'
import HeaderMenuProvider from './components/providers/data/HeaderMenuProvider'
import InstitutionProvider from './components/providers/data/InstitutionProvder'
import InstitutionsProvider from './components/providers/data/InstitutionsProvider'
import SidebarProvider from './components/providers/data/SidebarProvider'
import TrialsProvider from './components/providers/data/TrialsProvider'
import { createSentryAnonymousUser } from './errors/errors'
import { useAuthenticatedUser } from './hooks/authentication/useAuthenticatedUser'
import InstitutionCanonicalRedirectPage from './pages/InstitutionCanonicalRedirectPage'
import InstitutionDefaultRedirectPage from './pages/InstitutionDefaultRedirectPage'
import TrialsPage from './pages/TrialsPage'

const NoTrialHeading = styled(Typography)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    fontWeight: 400
}))

function RequirePrivilegedForAllInstitutions({ children }: { children: ReactNode }) {
    const { isBctStaff, isAdmin, isMedicalReviewer, isCentralScientist } = useAuthenticatedUser({ revalidateOnFocus: false, errorRetryCount: 0 });
    const isPrivilegedUser = (isBctStaff || isAdmin || isMedicalReviewer || isCentralScientist);

    if (!isPrivilegedUser) {
        return  <InstitutionDefaultRedirectPage />;
    }
    return <>{children}</>;
}

if (AppSettings.sentry.dsn) {
    const integrations = [
        // React Router V6 integration.
        // Helps with tracing URL changes and which URLs belong to the 
        // same route, for example:
        //    - https://test.com/form/1
        //    - https://test.com/form/2
        // both belong to the same route */form/*
        Sentry.reactRouterV6BrowserTracingIntegration({
            useEffect: useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
        // Browser Tracing integration
        // Helps with tracing URLs and other common browser "spans"
        Sentry.browserTracingIntegration(),
        // Replay integration
        // Records user actions and sends to Sentry.
        Sentry.replayIntegration({
            // Whether to mask all text on the page when recording user actions
            // When false only inputs are masked.
            // There is a separate setting to force unmasked inputs.
            maskAllText: false,
            // Whether to mask all images on the page when recording user 
            // actions.
            blockAllMedia: false,
            // Which URLs can the replay run on.
            // URL must contain one of the strings in the array or match one of
            // the regexes supplied
            networkDetailAllowUrls: [window.location.origin],
            // Capture the body of network requests
            // Sentry will still run data scrubbing on the bodies.
            networkCaptureBodies: true
        })
    ];
    if (AppSettings.sentry.enableReportABug) {
        integrations.push(Sentry.feedbackIntegration({
            // Disable the injection of the default widget
            autoInject: false,
            colorScheme: 'system',
            showBranding: false,
            showName: false,
            showEmail: false,
            tags: {
                Application: AppSettings.sentry.application,
                Customer: AppSettings.sentry.customer
            },
            useSentryUser: {
                name: 'fullName',
                email: 'email',
            },
        }));
    }
    Sentry.init({
        // The DSN to use when sending information to Sentry.
        // Identifies a specific project in Sentry.
        dsn: AppSettings.sentry.dsn,
        // Whether to send identifying information to Sentry such as
        // username, useragent, ip, etc
        sendDefaultPii: AppSettings.sentry.sendDefaultPii,
        // Whether to sent available performance metrics to Sentry.
        // These are the basic performance metrics and not the endepth
        // profiling metrics that take a lot more effort to setup.
        enableMetrics: AppSettings.sentry.enableMetrics,
        // Whether to attach stacktraces to actions sent to Sentry.
        attachStacktrace: AppSettings.sentry.attachStacktrace,
        // Whether @sentry/react is in debug mode, logging more information
        // to the console.
        debug: AppSettings.sentry.debug,
        // initialise integrations.
        integrations: integrations,
        // The environment to associate all action sent to Sentry with.
        environment: AppSettings.sentry.environment,
        // Release to associate all actions sent to Sentry with.
        release: AppSettings.sentry.release,
        // Percentage of captured traces that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        tracesSampleRate: AppSettings.sentry.tracesSampleRate,
        // Which URLs to trace / record in a trace.
        // Only trace internal URLs don't want to trace every SEO url
        // under the sun.
        tracePropagationTargets: [
            'localhost', 
            /^\//,
            new RegExp(`/^${window.location.origin}`)
        ],
        // Percentage of captured session replays that are sent to Sentry.
        // 1.0 = 100%, 0 = 0%
        replaysSessionSampleRate: AppSettings.sentry.replaysSessionSampleRate,
        // Percentage of captured session replays that are sent to Sentry.
        // when an error occurs
        // 1.0 = 100%, 0 = 0%
        replaysOnErrorSampleRate: AppSettings.sentry.replaysOnErrorSampleRate,
        // Enable logs to be sent to Sentry.
        enableLogs: AppSettings.sentry.enableLogs,
    });
    Sentry.setTag('Application', AppSettings.sentry.application);
    Sentry.setTag('Customer', AppSettings.sentry.customer);
    // Set user to anonymous at start up, this should be overriden by
    // React code if user information is available.
    Sentry.setUser(createSentryAnonymousUser());
}

const routes: RouteObject[] = [

    {
        path: "/",
        ErrorBoundary: RouteErrorBoundary,
        element: (
            <>
                <ScrollToTop />
                <InstitutionProvider>
                    <Layout />
                </InstitutionProvider>
            </>
        ),
        children: [
            {
                index: true,
                element: <InstitutionDefaultRedirectPage />
            },
            {
                path: 'all-institutions',
                element: (
                    <RequirePrivilegedForAllInstitutions>
                        <TrialsProvider>
                            <FavouritesProvider>
                                <TrialsPage />
                            </FavouritesProvider>
                        </TrialsProvider>
                    </RequirePrivilegedForAllInstitutions>
                )
            },
            {
                path: 'no-online-roles',
                element: (
                    <TrialsProvider>
                        <NoTrialHeading
                            variant="h2"
                        >
                            No trials to display
                        </NoTrialHeading>
                    </TrialsProvider>
                )
            },
            {
                path: ':institutionId',
                ErrorBoundary: RouteErrorBoundary,
                element: (
                    <TrialsProvider>
                        <FavouritesProvider>
                            <Outlet />
                        </FavouritesProvider>
                    </TrialsProvider>
                ),
                children: [
                    {
                        index: true,
                        element: <InstitutionCanonicalRedirectPage />
                    },
                    {
                        path: ':institutionName',
                        element: <TrialsPage />
                    }
                ]
            }
        ],
    },
    {
        path: '*',
        ErrorBoundary: RouteErrorBoundary,
        element: (
            <Navigate to="/" replace />
        ),
    }
];

const auth: AuthenticateResponse | undefined = undefined;

const router = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)(routes);

function App() {

    const authenticatedUser = useAuthenticatedUser({ revalidateOnFocus: true, errorRetryCount: 0,  refreshInterval: 600000});
    useEffect(() => {
        // no user, clear sentry user.
        if (!authenticatedUser.data) {
            // set Sentry User back to anonymous.
            Sentry.setUser(createSentryAnonymousUser());
            return;
        }
        // user, set sentry user.
        Sentry.setUser({
            id: authenticatedUser.data.userName,
            username: authenticatedUser.data.userName,
            fullName: authenticatedUser.data.displayName,
            authenticated: true
        })
        return () => {
            // set Sentry User back to anonymous.
            Sentry.setUser(createSentryAnonymousUser());
        }
    }, [authenticatedUser.data]);

    return (
        <StrictMode>
            <SWRConfig
                value={{
                    revalidateOnFocus: false,
                    errorRetryCount: 0
                }}
            >
                <LoadingProvider>
                    <ServiceStackClientProvider baseUrl={AppSettings.client?.baseUrl} basePath={AppSettings.client?.basePath}>
                        <ServerSentEventsProvider baseUrl={AppSettings.client?.eventStreamUrl ?? '/'} debug={AppSettings.client?.eventStreamDebug}>
                            <ChosenThemeSizeProvider>
                                <ChosenThemeProvider override={AppSettings.overrideTheme}>
                                    <ThemeProvider
                                        beforeThemeCreated={DefaultBeforeThemeCreated}
                                        afterThemeCreated={DefaultAfterThemeCreated}
                                    >
                                        <AuthenticatedUserProvider initial={auth}>
                                            <InstitutionsProvider>
                                                <HeaderMenuProvider>
                                                    <SidebarProvider>
                                                        <CssBaseline />
                                                        <RouterProvider router={router} />
                                                    </SidebarProvider>
                                                </HeaderMenuProvider>
                                            </InstitutionsProvider>
                                        </AuthenticatedUserProvider>
                                    </ThemeProvider>
                                </ChosenThemeProvider>
                            </ChosenThemeSizeProvider>
                        </ServerSentEventsProvider>
                    </ServiceStackClientProvider>
                </LoadingProvider>    
            </SWRConfig>
        </StrictMode>
    )
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}


document.addEventListener('DOMContentLoaded', function() {  
    const root = AppSettings.sentry.dsn ? 
        createRoot(
            document.getElementById('root')!,
            {
                // Callback called when React automatically recovers from errors.
                onRecoverableError: Sentry.reactErrorHandler(),
            }
        ) :
        createRoot(document.getElementById('root')!);
    root.render(
        <StrictMode>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                <App />
            </Sentry.ErrorBoundary>
        </StrictMode>
    )
});